import { App, debugFlags, TLShapePartial, uniqueId, useApp } from '@tldraw/editor'
import * as React from 'react'
import { track } from 'signia-react'
import { useToasts } from '../../hooks'
import { Button } from '../primitives'
import * as DropdownMenu from '../primitives/DropdownMenu'

let t = 0

function createNShapes(app: App, n: number) {
	const shapesToCreate: TLShapePartial[] = Array(n)
	const cols = Math.floor(Math.sqrt(n))

	for (let i = 0; i < n; i++) {
		t++
		shapesToCreate[i] = {
			id: app.createShapeId('box' + t),
			type: 'geo',
			x: (i % cols) * 132,
			y: Math.floor(i / cols) * 132,
		}
	}

	app.batch(() => {
		app.createShapes(shapesToCreate).setSelectedIds(shapesToCreate.map((s) => s.id))
	})
}

/** @public */
export const DebugPanel = React.memo(function DebugPanel({
	renderDebugMenuItems,
}: {
	renderDebugMenuItems: (() => React.ReactNode) | null
}) {
	return (
		<div className="tlui-debug-panel">
			<CurrentState />
			<ShapeCount />
			<DropdownMenu.Root id="debug">
				<DropdownMenu.Trigger>
					<Button icon="dots-horizontal" />
				</DropdownMenu.Trigger>
				<DropdownMenu.Content side="top" align="end" alignOffset={0}>
					<DebugMenuContent renderDebugMenuItems={renderDebugMenuItems} />
				</DropdownMenu.Content>
			</DropdownMenu.Root>
		</div>
	)
})

const CurrentState = track(function CurrentState() {
	const app = useApp()
	return <div className="tlui-debug-panel__current-state">{app.root.path.value}</div>
})

const ShapeCount = track(function ShapeCount() {
	const app = useApp()

	return <div>{app.renderingShapes.length} Shapes</div>
})

function DebugMenuContent({
	renderDebugMenuItems,
}: {
	renderDebugMenuItems: (() => React.ReactNode) | null
}) {
	const app = useApp()
	const { addToast } = useToasts()

	const [error, setError] = React.useState<boolean>(false)

	return (
		<DropdownMenu.Group>
			{(() => {
				if (error) throw Error('oh no!')
			})()}
			<DropdownMenu.Item
				onClick={() => {
					setError(true)
				}}
			>
				<span>Throw Error</span>
			</DropdownMenu.Item>
			<DropdownMenu.Item
				onClick={() => {
					addToast({
						id: uniqueId(),
						title: 'Something happened',
						description: 'Hey, attend to this thing over here. It might be important!',
						// icon?: string
						// title?: string
						// description?: string
						// actions?: TLToastAction[]
					})
				}}
			>
				<span>Show Toast</span>
			</DropdownMenu.Item>

			<DropdownMenu.Item
				onClick={() => {
					app.updateUserDocumentSettings({ isReadOnly: !app.isReadOnly })
				}}
			>
				<span>Toggle Read Only</span>
			</DropdownMenu.Item>
			<DropdownMenu.Item
				onClick={() => {
					debugFlags.peopleMenu.set(!debugFlags.peopleMenu.value)
				}}
			>
				<span>Toggle People Menu</span>
			</DropdownMenu.Item>
			<DropdownMenu.Item
				label="debug-menu.hard-reset"
				onClick={() => {
					;(window as any).__tldraw__hardReset()
				}}
			/>
			<DropdownMenu.Item label="debug-menu.create-shapes" onClick={() => createNShapes(app, 100)} />
			<DropdownMenu.Item
				onClick={() => {
					function countDescendants({ children }: HTMLElement) {
						let count = 0
						if (!children.length) return 0
						for (const el of [...(children as any)]) {
							count++
							count += countDescendants(el)
						}
						return count
					}

					const { selectedShapes } = app

					const shapes = selectedShapes.length === 0 ? app.renderingShapes : selectedShapes

					const elms = shapes.map(
						(shape) => (document.getElementById(shape.id) as HTMLElement)!.parentElement!
					)

					let descendants = elms.length

					for (const elm of elms) {
						descendants += countDescendants(elm)
					}

					window.alert(`Shapes ${shapes.length}, DOM nodes:${descendants}`)
				}}
			>
				<span>Count Shapes and Nodes</span>
			</DropdownMenu.Item>
			{renderDebugMenuItems?.()}
		</DropdownMenu.Group>
	)
}
